import request from '@/utils/request';
import { apiErrorsOf } from '@/utils/requestHandling';
import type { GoalStats, GoalAchievements } from './types';

export function extractPayload<T>(data: unknown): T {
  if (Array.isArray(data)) {
    return data as T;
  }
  if (data && typeof data === 'object' && 'payload' in data) {
    return (data as { payload: T }).payload;
  }
  return data as T;
}

/**
 * CO-569: this read `error.response.data.errors` only. The axios interceptor rejects with the
 * PARSED `Result` body, so `response` is undefined on every refusal that came through it and the
 * server's reason was replaced by the generic fallback. `apiErrorsOf` walks every wrapper shape
 * (including the non-enumerable `__apiErrors` stamp) and this now returns EVERY violation, not the
 * first. No `t` here: this module is below the React tree, so the fallbacks stay English as before.
 */
function extractApiError(error: unknown, fallback: string): string {
  const descriptions = apiErrorsOf(error)
    .map((e) => e.description)
    .filter((d): d is string => typeof d === 'string' && d.trim().length > 0);
  if (descriptions.length > 0) return descriptions.join('\n');
  const err = error as { message?: string };
  return err?.message || fallback;
}

export async function getGoalDashboardStats(): Promise<GoalStats> {
  try {
    const response = await request.get(`Goals/dashboard/stats`);
    return extractPayload<GoalStats>(response.data);
  } catch (error: unknown) {
    throw new Error(extractApiError(error, 'Failed to fetch goal dashboard statistics'));
  }
}

export async function getGoalProgressIndicators(): Promise<unknown[]> {
  try {
    const response = await request.get(`Goals/dashboard/progress-indicators`);
    return extractPayload<unknown[]>(response.data);
  } catch (error: unknown) {
    throw new Error(extractApiError(error, 'Failed to fetch goal progress indicators'));
  }
}

export async function getSurveyDashboardData(): Promise<Record<string, unknown>> {
  try {
    const response = await request.get(`Survey/GetStats`);
    return extractPayload<Record<string, unknown>>(response.data);
  } catch (error: unknown) {
    throw new Error(extractApiError(error, 'Failed to fetch survey data'));
  }
}

export async function getGoalAchievementProgress(): Promise<GoalAchievements> {
  try {
    const response = await request.get(`Goals/achievements/progress`);
    return extractPayload<GoalAchievements>(response.data);
  } catch (error: unknown) {
    throw new Error(extractApiError(error, 'Failed to fetch goal achievement progress'));
  }
}
